import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Button } from "../components/ui/Button";
import { api } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { dayAndTime } from "../lib/time";
import type { FailedReply } from "../types";

/**
 * Every reply that did not go out, in one list, to send again in one go.
 *
 * Try again already existed on each failed message, which is fine for one and
 * hopeless for eleven spread across eleven tickets -- what a channel's
 * credential dying for three days leaves behind. The banner says how many;
 * this is where they are dealt with.
 *
 * Everything starts ticked, because the common case is "the channel is fixed,
 * send the lot". But a reply goes out exactly as it was written, however long
 * ago that was, so the age is on every row and anything since overtaken -- an
 * agent who gave up and wrote again -- can be unticked first.
 *
 * Sent one at a time through the same `retrySend` the ticket's button uses,
 * so the server's refusal to re-queue a reply still in flight applies here
 * too. One failure does not stop the rest; each row says how it went.
 */
export function FailedReplies() {
  const queryClient = useQueryClient();
  const replies = useQuery({
    queryKey: queryKeys.failedReplies,
    queryFn: api.failedReplies,
  });

  const rows = useMemo(() => replies.data ?? [], [replies.data]);
  const [unticked, setUnticked] = useState<Set<number>>(new Set());
  const [outcome, setOutcome] = useState<Map<number, "queued" | string>>(new Map());
  const [sending, setSending] = useState(false);

  /* Forget choices about rows that have gone, so a reply failing again later
     starts ticked like any other. */
  useEffect(() => {
    const present = new Set(rows.map((row) => row.messageId));
    setUnticked((previous) => new Set([...previous].filter((id) => present.has(id))));
  }, [rows]);

  /* A row already queued stays on screen until the list is refetched, so it
     must not be counted or sent twice. */
  const pending = rows.filter((row) => outcome.get(row.messageId) !== "queued");
  const chosen = pending.filter((row) => !unticked.has(row.messageId));

  const toggle = (id: number): void =>
    setUnticked((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allTicked = pending.length > 0 && chosen.length === pending.length;
  const toggleAll = (): void =>
    setUnticked(allTicked ? new Set(pending.map((row) => row.messageId)) : new Set());

  const sendChosen = async (): Promise<void> => {
    setSending(true);
    for (const row of chosen) {
      try {
        await api.retrySend(row.messageId);
        setOutcome((previous) => new Map(previous).set(row.messageId, "queued"));
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setOutcome((previous) => new Map(previous).set(row.messageId, message));
      }
    }
    setSending(false);
    void queryClient.invalidateQueries({ queryKey: queryKeys.channelAlerts });
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 pb-10 pt-4 sm:px-6">
        <header className="mb-3 flex flex-wrap items-center gap-2">
          <h1 className="text-base font-semibold text-content">Failed replies</h1>
          {rows.length > 0 ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
              {rows.length}
            </span>
          ) : null}
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setOutcome(new Map());
                void replies.refetch();
              }}
              disabled={sending || replies.isFetching}
            >
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => void sendChosen()}
              disabled={sending || chosen.length === 0}
            >
              {sending
                ? "Sending…"
                : chosen.length === pending.length
                  ? `Send all again (${chosen.length})`
                  : `Send ${chosen.length} again`}
            </Button>
          </div>
        </header>

        <p className="mb-4 text-xs text-muted">
          Replies from the last 30 days that ran out of attempts, on the channels
          you work. Each goes out exactly as it was written, so untick any that
          have been overtaken. Fix a broken channel first, or they will fail
          again.
        </p>

        {replies.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : replies.isError ? (
          <p className="text-sm text-red-700 dark:text-red-300">
            Could not load failed replies: {replies.error.message}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted">Nothing failed. Every reply went out.</p>
        ) : (
          <table className="w-full table-fixed text-left text-sm">
            <thead className="text-xs text-muted">
              <tr className="border-b border-line">
                <th className="w-8 py-1.5">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allTicked}
                    onChange={toggleAll}
                    disabled={sending || pending.length === 0}
                  />
                </th>
                <th className="py-1.5 font-medium">Ticket</th>
                <th className="hidden w-40 py-1.5 font-medium sm:table-cell">From</th>
                <th className="hidden w-32 py-1.5 font-medium md:table-cell">Written by</th>
                <th className="w-28 py-1.5 font-medium">Written</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.messageId}
                  row={row}
                  ticked={!unticked.has(row.messageId)}
                  onToggle={() => toggle(row.messageId)}
                  outcome={outcome.get(row.messageId)}
                  disabled={sending}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({
  row,
  ticked,
  onToggle,
  outcome,
  disabled,
}: {
  row: FailedReply;
  ticked: boolean;
  onToggle: () => void;
  outcome: "queued" | string | undefined;
  disabled: boolean;
}) {
  const queued = outcome === "queued";

  return (
    <tr className="border-b border-line align-top">
      <td className="py-2">
        <input
          type="checkbox"
          aria-label={`Send #${row.ticketId} again`}
          checked={ticked && !queued}
          onChange={onToggle}
          disabled={disabled || queued}
        />
      </td>
      <td className="min-w-0 py-2 pr-2">
        <Link
          to={`/v/open/${row.ticketId}`}
          className="block truncate text-content hover:text-brand-600 dark:hover:text-brand-400"
        >
          <span className="text-muted">#{row.ticketId}</span> {row.subject ?? "(no subject)"}
        </Link>
        {queued ? (
          <p className="text-xs text-emerald-700 dark:text-emerald-300">
            Queued. It will show as sent on the ticket once it goes.
          </p>
        ) : outcome ? (
          <p className="text-xs text-red-700 dark:text-red-300">{outcome}</p>
        ) : row.deliveryError ? (
          <p className="truncate text-xs text-muted" title={row.deliveryError}>
            {row.deliveryError}
          </p>
        ) : null}
      </td>
      <td className="hidden truncate py-2 pr-2 font-mono text-xs text-muted sm:table-cell">
        {row.channelAddress}
      </td>
      <td className="hidden truncate py-2 pr-2 text-xs text-muted md:table-cell">
        {row.authorName ?? "—"}
      </td>
      <td className="py-2 text-xs text-muted">{dayAndTime(row.sentAt)}</td>
    </tr>
  );
}
